import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import * as eficienciaApi from '@/api/eficiencia.api';
import type { FilaDeEficiencia } from '@/api/eficiencia.api';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { money } from '@/lib/money';

/**
 * EFICIENCIA — qué anuncio y qué vendedor venden de verdad.
 *
 * Portado de Tony Boga el 4-nov-2026 (orden de Kervens). La LÓGICA es la de
 * allá; la PANTALLA se rehízo con los componentes de Osito, porque los dos
 * paneles no comparten librería y copiarla tal cual habría metido una piel
 * ajena en el sistema.
 *
 * El embudo: chats nuevos → pasaron a vendedor → compraron. Al lado, las
 * ventas confirmadas y lo vendido. Con el costo del anuncio anotado salen el
 * costo por venta y el retorno. Donde no hay dato va «—», nunca un cero
 * inventado: un cero dice «no vendió» y un guion dice «no se sabe».
 */

const PERIODOS = [
  { dias: 7, etiqueta: 'Últimos 7 días' },
  { dias: 30, etiqueta: 'Últimos 30 días' },
  { dias: 90, etiqueta: 'Últimos 90 días' },
];

const pct = (n: number | null) => (n == null ? '—' : `${n} %`);
const veces = (n: number | null) => (n == null ? '—' : `${n.toLocaleString('es-DO')}×`);
const pesos = (n: number | null) => (n == null ? '—' : money(n, 'RD$'));
const entero = (n: number) => n.toLocaleString('es-DO');

function Tabla({
  filas,
  conCosto,
  onCosto,
}: {
  filas: FilaDeEficiencia[];
  conCosto: boolean;
  onCosto?: (f: FilaDeEficiencia) => void;
}) {
  if (filas.length === 0) {
    return <EmptyState title="Sin movimiento en este período" />;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] text-sm">
        <thead>
          <tr className="border-b border-surface-border text-left text-xs uppercase tracking-wide text-slate-500">
            <th className="px-3 py-2 font-medium">{conCosto ? 'Anuncio' : 'Vendedor'}</th>
            <th className="px-3 py-2 text-right font-medium">Chats</th>
            <th className="px-3 py-2 text-right font-medium">Pasaron</th>
            <th className="px-3 py-2 text-right font-medium">Compraron</th>
            <th className="px-3 py-2 text-right font-medium">Cierre</th>
            <th className="px-3 py-2 text-right font-medium">Ventas</th>
            <th className="px-3 py-2 text-right font-medium">Vendido</th>
            {conCosto && (
              <>
                <th className="px-3 py-2 text-right font-medium">Costo</th>
                <th className="px-3 py-2 text-right font-medium">Por venta</th>
                <th className="px-3 py-2 text-right font-medium">Retorno</th>
                <th className="px-3 py-2" />
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => (
            <tr key={f.clave} className="border-b border-surface-border/60 last:border-0">
              <td className="px-3 py-2">
                <span className="font-medium">{f.nombre}</span>
                {f.vencidos > 0 && (
                  <span className="ml-2 text-xs text-amber-600">{f.vencidos} vencidos</span>
                )}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{entero(f.chats)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{entero(f.pasaron)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{entero(f.compraron)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{pct(f.tasaDeCierre)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{entero(f.ventas)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{money(f.vendido, 'RD$')}</td>
              {conCosto && (
                <>
                  <td className="px-3 py-2 text-right tabular-nums">{pesos(f.costo)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{pesos(f.costoPorVenta)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{veces(f.retorno)}</td>
                  <td className="px-3 py-2 text-right">
                    {f.clave !== '__sin__' && onCosto && (
                      <button
                        type="button"
                        onClick={() => onCosto(f)}
                        className="text-slate-400 hover:text-slate-700"
                        aria-label={`Anotar el costo de ${f.nombre}`}
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Eficiencia() {
  const [dias, setDias] = useState(30);
  const [editando, setEditando] = useState<FilaDeEficiencia | null>(null);
  const [costo, setCosto] = useState('');
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['eficiencia', dias],
    queryFn: () => eficienciaApi.getEficiencia(dias),
  });

  const guardar = useMutation({
    mutationFn: (datos: { sourceId: string; titulo?: string; costo: number }) =>
      eficienciaApi.ponerCostoDeAnuncio(datos),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['eficiencia'] });
      setEditando(null);
      setCosto('');
    },
  });

  const t = data?.total;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Eficiencia"
        description="Qué anuncio y qué vendedor venden de verdad"
        action={
          <Select value={String(dias)} onChange={(e) => setDias(Number(e.target.value))}>
            {PERIODOS.map((p) => (
              <option key={p.dias} value={p.dias}>
                {p.etiqueta}
              </option>
            ))}
          </Select>
        }
      />

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      )}

      {t && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          {[
            { k: 'Chats nuevos', v: entero(t.chats) },
            { k: 'Pasaron a vendedor', v: entero(t.pasaron) },
            { k: 'Compraron', v: entero(t.compraron) },
            { k: 'Vendido', v: money(t.vendido, 'RD$') },
            { k: 'Invertido en anuncios', v: pesos(t.costo) },
          ].map((c) => (
            <Card key={c.k}>
              <CardBody>
                <p className="text-xs uppercase tracking-wide text-slate-500">{c.k}</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">{c.v}</p>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {data && (
        <Card>
          <CardBody>
            <Tabs defaultValue="anuncio">
              <TabsList>
                <TabsTrigger value="anuncio">Por anuncio</TabsTrigger>
                <TabsTrigger value="vendedor">Por vendedor</TabsTrigger>
              </TabsList>
              <TabsContent value="anuncio">
                <Tabla
                  filas={data.porAnuncio}
                  conCosto
                  onCosto={(f) => {
                    setEditando(f);
                    setCosto(f.costo == null ? '' : String(f.costo));
                  }}
                />
                {t?.costo == null && (
                  <p className="mt-3 text-sm text-slate-500">
                    Todavía no hay costos anotados. Sin lo que se pagó por cada anuncio, la tabla
                    dice cuántos clientes trajo pero no si valió la pena. Anótalos con el lápiz.
                  </p>
                )}
              </TabsContent>
              <TabsContent value="vendedor">
                <Tabla filas={data.porVendedor} conCosto={false} />
              </TabsContent>
            </Tabs>
          </CardBody>
        </Card>
      )}

      <Modal
        open={editando != null}
        onClose={() => setEditando(null)}
        title={editando ? `Costo de ${editando.nombre}` : ''}
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-500">
            Lo que se pagó por este anuncio en el período, en pesos enteros.
          </p>
          <Input
            type="number"
            min={0}
            step={1}
            value={costo}
            onChange={(e) => setCosto(e.target.value)}
            placeholder="0"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setEditando(null)}>
              Cancelar
            </Button>
            <Button
              disabled={!/^\d+$/.test(costo) || guardar.isPending}
              onClick={() =>
                editando &&
                guardar.mutate({
                  sourceId: editando.clave,
                  titulo: editando.nombre,
                  costo: Number(costo),
                })
              }
            >
              Guardar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
